import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { DataTableBody, DataTableCell, DataTableHead, DataTableHeader, DataTableRow } from "@insite/mobius/index";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getDisplayBreakPrices } from "@SupplyLand/Utilities/minimumOrderBreaks";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showPrice: boolean;
    showAddToList: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps;

export interface ProductListActionsStyles {
    wrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
    quantityOrdered?: TextFieldProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    addToListLink?: ProductAddToListLinkStyles;
    addToListWrapper?: InjectableCss;
    breakQtyHeader?: DataTableHeaderProps;
    breakPriceHeader?: DataTableHeaderProps;
    savingsHeader?: DataTableHeaderProps;
    clearanceHeader?: DataTableHeaderProps;
    breakQtyCell?: DataTableCellBaseProps;
    breakPriceCell?: DataTableCellBaseProps;
    savingsCell?: DataTableCellBaseProps;
    clearanceCell?: DataTableCellBaseProps;
    slashedPrice?: InjectableCss;
    table?: DataTableProps;
    tableHead?: DataTableHeadProps;
    tableBody?: DataTableBodyProps;
    tableRow?: DataTableRowProps;
}

// The actions column can be as narrow as ~220px on phones (xs) and ~240px at lg, and the
// table gains a third column when a product has clearance pricing, so cells scale their
// padding/font by breakpoint and headers are allowed to wrap onto a second line.
const tableCellResponsiveCss = css`
    height: 30px;
    white-space: normal;
    line-height: 1.2;
    ${({ theme }) =>
        breakpointMediaQueries(theme, [
            css`
                padding: 4px;
                font-size: 12px;
            `,
            css`
                padding: 4px 8px;
                font-size: 14px;
            `,
            css`
                padding: 4px 8px;
                font-size: 14px;
            `,
            css`
                padding: 4px 6px;
                font-size: 13px;
            `,
            css`
                padding: 4px 6px;
                font-size: 14px;
            `,
        ])}
`;

// The theme's data table header typography uses a fixed 15px size; inherit from the
// header cell instead so the responsive sizes above apply.
const headerTypographyProps: TypographyPresentationProps = {
    css: css`
        font-size: inherit;
        line-height: inherit;
    `,
};

export const actionsStyles: ProductListActionsStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
        `,
    },
    unitOfMeasureSelect: {
        cssOverrides: {
            formField: css`
                margin-top: 20px;
            `,
        },
    },
    addToCartWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            margin-top: 10px;
        `,
    },
    addToCartButton: {
        css: css`
            flex-grow: 1;
            width: auto;
            min-width: 0;
            color: black;
        `,
    },
    quantityOrdered: {
        cssOverrides: {
            formField: css`
                margin-right: 10px;
                width: 4.1rem;
                min-width: 4.1rem;
            `,
        },
    },
    addToListWrapper: {
        css: css`
            margin-top: 20px;
            min-height: 2rem;
        `,
    },
    table: {
        css: css`
            width: 100%;
            table-layout: fixed;
        `,
    },
    breakQtyHeader: {
        alignX: "center",
        typographyProps: headerTypographyProps,
        css: css`
            width: 26%;
            ${tableCellResponsiveCss}
        `,
    },
    breakPriceHeader: {
        alignX: "center",
        typographyProps: headerTypographyProps,
        css: tableCellResponsiveCss,
    },
    savingsHeader: {
        alignX: "center",
        typographyProps: headerTypographyProps,
        css: tableCellResponsiveCss,
    },
    breakQtyCell: {
        alignX: "center",
        css: tableCellResponsiveCss,
    },
    breakPriceCell: {
        alignX: "center",
        css: tableCellResponsiveCss,
    },
    savingsCell: {
        alignX: "center",
        css: tableCellResponsiveCss,
    },
    clearanceHeader: {
        alignX: "center",
        typographyProps: headerTypographyProps,
        css: css`
            ${tableCellResponsiveCss}
            color: #FF0000;
            font-weight: 700;
        `,
    },
    clearanceCell: {
        alignX: "center",
        css: css`
            ${tableCellResponsiveCss}
            color: #FF0000;
            font-weight: 600;
        `,
    },
    slashedPrice: {
        css: css`
            display: inline-block;
            text-decoration: line-through;
            color: #6b6b6b;
        `,
    },
};

const styles = actionsStyles;

const ProductListActions: FC<Props> = ({ productContext, showPrice, showAddToList }) => {
    const pricing = productContext.productInfo?.pricing;
    if (!pricing) {
        return null;
    }
    const breakPrices = getDisplayBreakPrices(
        pricing.unitRegularBreakPrices,
        productContext.product,
        pricing.unitOfMeasure,
    );
    // When the product is on sale, the regular break prices are shown slashed out and the
    // discounted (actual) break prices are displayed in a "Clearance" column, matched by break qty.
    const clearanceBreakPrices = pricing.isOnSale ? pricing.actualBreakPrices : null;
    const showClearance = !!clearanceBreakPrices && clearanceBreakPrices.length > 0;
    const clearancePriceByQty = new Map<number, string>();
    clearanceBreakPrices?.forEach(breakPrice => clearancePriceByQty.set(breakPrice.breakQty, pricing.actualPriceDisplay || ""));
    return (
        <StyledWrapper {...styles.wrapper}>
            {showPrice && breakPrices.length > 0 && (
                <DataTable {...styles.table}>
                    <DataTableHead {...styles.tableHead}>
                        <DataTableHeader {...styles.breakQtyHeader}>{translate("Qty Breaks")}</DataTableHeader>
                        <DataTableHeader {...styles.breakPriceHeader}>{translate("Price Per")} {pricing?.unitOfMeasure}</DataTableHeader>
                        {showClearance && (
                            <DataTableHeader {...styles.clearanceHeader}>{translate("Clearance")}</DataTableHeader>
                        )}
                    </DataTableHead>
                    <DataTableBody {...styles.tableBody}>
                        {breakPrices.map(breakPrice => (
                            <DataTableRow
                                {...styles.tableRow}
                                key={breakPrice.breakQty}
                                data-test-selector="breakPriceRow"
                            >
                                <DataTableCell {...styles.breakQtyCell} data-test-selector="breakQty">
                                    {breakPrice.breakQty}
                                </DataTableCell>
                                <DataTableCell {...styles.breakPriceCell} data-test-selector="price">
                                    {showClearance ? (
                                        <StyledWrapper {...styles.slashedPrice}>
                                            <span
                                                dangerouslySetInnerHTML={{ __html: breakPrice.breakPriceDisplay || "" }}
                                            />
                                        </StyledWrapper>
                                    ) : (
                                        <span dangerouslySetInnerHTML={{ __html: breakPrice.breakPriceDisplay || "" }} />
                                    )}
                                </DataTableCell>
                                {showClearance && (
                                    <DataTableCell {...styles.clearanceCell} data-test-selector="clearancePrice">
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html:
                                                    clearancePriceByQty.get(breakPrice.breakQty) ||
                                                    pricing.actualPriceDisplay ||
                                                    "",
                                            }}
                                        />
                                    </DataTableCell>
                                )}
                            </DataTableRow>
                        ))}
                    </DataTableBody>
                </DataTable>
            )}
            <ProductUnitOfMeasureSelect extendedStyles={styles.unitOfMeasureSelect} />
            <StyledWrapper {...styles.addToCartWrapper}>
                <ProductQuantityOrdered extendedStyles={styles.quantityOrdered} label="" />
                <ProductAddToCartButton
                    data-test-selector={`actionsAddToCart${productContext.product.id}`}
                    extendedStyles={styles.addToCartButton}
                />
            </StyledWrapper>
            {showAddToList && (
                <StyledWrapper {...styles.addToListWrapper}>
                    <ProductAddToListLink
                        data-test-selector={`actionsAddToList${productContext.product.id}`}
                        extendedStyles={styles.addToListLink}
                    />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(ProductListActions));
